<script setup>
import { ScrollTrigger } from '~/vendors/gsap'
import { useContentful } from '~~/composables/useContentful'
import { useLiveEventStore } from '~~/store/event'
import { onLoadAnimationEnd } from '~/composables/onLoadAnimationEnd'
import homeQuery from '~/graphql/page-home'

import GlobalEmitter from '~~/glxp/utils/emitter'

const el = ref(null)
const route = useRoute()

/**
 * Page Data
 */
const contentful = useContentful()
const { data } = await useAsyncData(async () => {
  const response = await contentful.graphql(homeQuery)
  const json = await response.json()
  return json.data.specialPages
})

useHead({
  title: 'Home | Virgin Galactic',
  meta: data.value.seo && [
    { name: 'title', content: () => data.value.seo.title },
    { name: 'description', content: () => data.value.seo.description },
    data.value.seo.keywords && {
      name: 'keywords',
      content: () => data.value.seo.keywords.join(', '),
    },
    (data.value.seo.noindex || data.value.seo.nofollow) && {
      name: 'robots',
      content: () =>
        [
          data.value?.seo?.noindex ? 'noindex' : null,
          data.value?.seo?.nofollow ? 'nofollow' : null,
        ]
          .filter((value) => value !== null)
          .join(','),
    },
  ],
})

/**
 * Live event
 */
const liveEventStore = useLiveEventStore()
const liveEvent = computed(() => liveEventStore.liveEvent)
const posterTransform = reactive({
  scale: 1,
  translation: 0,
})
function posterUpdate({ scale, translation }) {
  posterTransform.scale = scale * 1.1 // ending scale needs to be at 1.1 to cover the translation
  posterTransform.translation = translation
}

/**
 * Spaceport scroll trigger
 */
let st

function initSpaceportProgress() {
  st = ScrollTrigger.create({
    trigger: '.clipped-section--slug-overview-effect',
    start: 'bottom bottom',
    endTrigger: '.clipped-section--slug-coda',
    end: 'bottom bottom',
    // markers: true,
    onEnter: () => {
      GlobalEmitter.emit('webgl_raf_start')
    },
    onLeaveBack: () => {
      GlobalEmitter.emit('webgl_raf_stop')
    },
    onUpdate: ({ progress }) => {
      GlobalEmitter.emit('webgl_home_spaceport_progress', { progress })
    },
  })
}

let st2
function initGradientProgress() {
  st2 = ScrollTrigger.create({
    trigger: '.clipped-section--slug-overview-effect',
    start: 'top bottom',
    onEnter: () => {
      const gradientEl = el.value.querySelector('.page-home__gradient-overlay')
      gradientEl.style.opacity = 1
    },
    onLeaveBack: () => {
      const gradientEl = el.value.querySelector('.page-home__gradient-overlay')
      gradientEl.style.opacity = 0
    },
  })
}

onLoadAnimationEnd(() => {
  initSpaceportProgress()
  initGradientProgress()
  GlobalEmitter.emit('webgl_raf_start')
})

onBeforeUnmount(() => {
  st?.kill()
  st2?.kill()
})

onLoadAnimationEnd(async () => {
  if (route.hash) {
    const scrollToEl = document.querySelector(route.hash)

    if (scrollToEl) {
      const { top } = scrollToEl.getBoundingClientRect()
      await new Promise((resolve) => setTimeout(resolve, 0))
      window.scrollTo({
        top: top + document.documentElement.scrollTop,
        behavior: 'smooth',
      })
    }
  }
})
</script>

<template>
  <article class="page-home" ref="el">
    <template v-if="data">
      <HomeHero :eyebrow="data.heroEyebrow" :headline="data.heroHeadline" />

      <div class="page-home__body page-body">
        <FloatingBar class="page-home__floating-bar" />

        <div class="page-home__gradient-overlay" />

        <div v-if="data.sectionsCollection.total" class="page-home__sections">
          <template v-if="liveEvent">
            <div id="featured" />
            <ClippedSection slug="featured" @bg-update="posterUpdate">
              <div class="page-home__featured section">
                <VideoPlayer
                  class="page-home__featured-player"
                  video-gtm-class="gtm-vgms-home-spaceline-for-earth-video-unity-25"
                  :title="liveEvent.title"
                  :src="liveEvent.youtubeUrl"
                  :poster="liveEvent.posterImage?.url"
                  :poster-mobile="liveEvent.posterImageMobile?.url"
                  autoplay
                  inline
                  featured
                  :isLivestream="liveEvent.isLivestream"
                  :posterTransform="posterTransform"
                />
              </div>
            </ClippedSection>
          </template>
          <HomeSection
            v-for="(item, index) in data.sectionsCollection.items"
            :key="item.sys.id"
            :title="item.title"
            :slug="item.slug"
            :eyebrow="item.eyebrow"
            :headline="item.headline"
            :headline-size="item.headlineSize"
            :copy="item.copy"
            :gallery="item.galleryCollection.items"
            :background-image="item.backgroundImage?.url"
            :background-image-mobile="item.backgroundImageMobile?.url"
            :call-to-action="item.callToAction"
            :call-to-action-gtm-class="item.callToActionGtmClass"
            :video-card="item.videoCard"
            :video-gtm-class="item.videoGtmClass"
            :is-last="index === data.sectionsCollection.items.length - 1"
          />
        </div>
      </div>
    </template>
  </article>

  <ClientOnly>
    <Teleport v-if="data" to="#super-footer">
      <FooterLinks
        class="page-home__footer-links"
        v-if="data.footerCtAsCollection"
        :items="data.footerCtAsCollection.items"
      />
    </Teleport>
  </ClientOnly>
</template>

<style lang="scss">
// Vendors
@import 'styles/vendors/sass-mq/mq';

// Utils
@import 'styles/utils/variables';
@import 'styles/utils/functions';

.page-home {
  &__featured {
    background: #080711;
  }

  &__body {
    position: relative;
    display: grid;
  }

  &__floating-bar,
  &__sections,
  &__gradient-overlay {
    grid-row: 1;
    grid-column: 1;
  }

  &__sections {
    display: flex;
    flex-direction: column;
  }

  &__footer-links-cta {
    margin-top: 48px;
  }

  &__gradient-overlay {
    position: sticky;
    top: 0;
    left: 0;
    width: 100%;
    // height: var(--real-height, 100vh);
    height: 100vh;
    background: radial-gradient(
        117.57% 117.57% at 50% 50%,
        rgba(17, 9, 32, 0) 0%,
        rgba(17, 9, 32, 0.3) 100%
      ),
      linear-gradient(18.73deg, #0e061e 2.78%, rgba(14, 5, 31, 0) 47.69%);
    pointer-events: none;
    z-index: 0;
    opacity: 0;
  }
}
</style>
